import { memo, useEffect, useMemo, useState } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Drawer, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MenuCard from './MenuCard';
import MenuList from '../MenuList';
import LogoSection from '../LogoSection';
import MiniDrawerStyled from './MiniDrawerStyled';

import LAYOUT_CONST from 'constant';
import useConfig from 'hooks/useConfig';
import { drawerWidth } from 'store/constant';

import { useDispatch, useSelector } from 'store';
import { openDrawer } from 'store/slices/menu';
import useAuth from 'hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { LogoutOutlined } from '@mui/icons-material';
import { FormattedMessage } from 'react-intl';
import KeyFormDialog from './CDkeyDialog';
import PromotionDialog from './PromotionDialog';
import MobilePromotionDialog from './MobilePromotionDialog';
import CdKeyIcon from '../../../assets/images/promotion/cdk.png';
import ArrowIcon from '../../../assets/images/promotion/arrow.png';

import './index.less';

// ==============================|| SIDEBAR DRAWER ||============================== //

const Sidebar = () => {
    const theme = useTheme();
    const matchUpMd = useMediaQuery(theme.breakpoints.up('md'));
    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { drawerOpen } = useSelector((state) => state.menu);

    const { layout, drawerType } = useConfig();
    const { logout, user, cdkeys, promotion } = useAuth();
    const [show, setShow] = useState(false);
    const [open, setOpen] = useState(false);
    const [showPromotion, setShowPromotion] = useState(false);

    const handleClose = () => {
        setOpen(false);
    };

    const logo = useMemo(
        () => (
            <Box sx={{ display: 'flex', p: 2 }}>
                <LogoSection />
            </Box>
        ),
        []
    );

    const handleOpenPromotion = () => {
        setShowPromotion(true);
    };

    const handleClosePromotion = () => {
        setShowPromotion(false);
    };

    useEffect(() => {
        if (cdkeys) {
            let filterArr = cdkeys?.filter((item: any) => item.state === 0);

            setShow(filterArr.length > 0);
            if (filterArr.length > 0) {
                setTimeout(() => {
                    setShow(false);
                }, 3000);
            }
        }
    }, [cdkeys]);

    const drawerContent = (
        <>
            <MenuList />
            {layout === LAYOUT_CONST.VERTICAL_LAYOUT && drawerOpen && !matchDownMd && user.stat && <MenuCard />}
            <ListItemButton
                sx={{
                    borderRadius: '8px',
                    mb: 0,
                    alignItems: 'flex-start',
                    backgroundColor: 'rgba(255, 220, 52, 0.10)',
                    py: 1,
                    pl: 3,
                    paddingTop: '10px',
                    paddingBottom: '10px',
                    marginBottom: '4px',

                    '&:hover': {
                        background: 'rgba(255, 220, 52, 0.10)'
                    }
                }}
                selected={false}
                onClick={() => {
                    setOpen(true);
                }}
            >
                {show && (
                    <section className="promotion-float">
                        <div className="cont">
                            <FormattedMessage id="cdkey.title" values={{ key: <label className="key">CDKey</label> }} />
                        </div>
                    </section>
                )}
                <ListItemIcon
                    sx={{
                        my: 'auto',
                        minWidth: 36
                    }}
                >
                    <img width={'32px'} height={'32px'} style={{ marginTop: '-3px' }} src={CdKeyIcon} alt="cdket" />
                </ListItemIcon>
                {drawerOpen &&
                    <>
                        <ListItemText
                            primary={
                                <Typography variant="body1" color="#616161">
                                    <FormattedMessage id="cdkey.menu" />
                                </Typography>
                            }
                        />
                        <ListItemIcon
                            sx={{
                                my: 'auto',
                                minWidth: 36
                            }}
                        >
                            <img width={'32px'} height={'24px'} src={ArrowIcon} alt="cdket" />
                        </ListItemIcon>
                    </>
                }
            </ListItemButton>
            <ListItemButton
                sx={{
                    borderRadius: '8px',
                    mb: 0,
                    alignItems: 'flex-start',
                    backgroundColor: 'primary',
                    py: 1,
                    pl: 3,
                    '&:hover': {
                        background: theme.palette.primary.light
                    }
                }}
                selected={false}
                onClick={() => {
                    logout();
                    navigate('/login');
                }}
            >
                <ListItemIcon
                    sx={{
                        my: 'auto',
                        minWidth: 36
                    }}
                >
                    <LogoutOutlined
                        style={{
                            color: '#616161'
                        }}
                    />
                </ListItemIcon>
                {drawerOpen &&
                    <ListItemText
                        primary={
                            <Typography variant="body1" color="#616161">
                                <FormattedMessage id="logout" />
                            </Typography>
                        }
                    />
                }
            </ListItemButton>
            {/* {layout === LAYOUT_CONST.VERTICAL_LAYOUT && drawerOpen && (
                <Stack direction="row" justifyContent="center" sx={{ mb: 2, mt: 2 }}>
                    <Chip label={import.meta.env.VITE_APP_VERSION} disabled chipcolor="secondary" size="small" sx={{ cursor: 'pointer' }} />
                </Stack>
            )} */}
            {promotion && promotion.showCountdown && (
                <section className="side-promotion" onClick={handleOpenPromotion}>
                    <div className="cont">
                        <FormattedMessage id="promotion.title4" />
                    </div>
                </section>
            )}
        </>
    );

    const drawerSX = {
        paddingLeft: drawerOpen ? '16px' : 0,
        paddingRight: drawerOpen ? '16px' : 0,
        marginTop: drawerOpen ? 0 : '20px'
    };

    const drawer = useMemo(
        () => (
            <>
                {matchDownMd ? (
                    <Box sx={drawerSX}>{drawerContent}</Box>
                ) : (
                    <PerfectScrollbar
                        component="div"
                        style={{
                            height: !matchUpMd ? 'calc(100vh - 56px)' : 'calc(100vh - 88px)',
                            ...drawerSX
                        }}
                    >
                        {drawerContent}
                    </PerfectScrollbar>
                )}
            </>
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [matchUpMd, drawerOpen, drawerType, show]
    );

    return (
        <Box component="nav" sx={{ flexShrink: { md: 0 }, width: matchUpMd ? drawerWidth : 'auto' }} aria-label="mailbox folders">
            {matchDownMd || (drawerType === LAYOUT_CONST.MINI_DRAWER && drawerOpen) ? (
                <Drawer
                    variant={matchUpMd ? 'persistent' : 'temporary'}
                    anchor="left"
                    open={drawerOpen}
                    onClose={() => dispatch(openDrawer(!drawerOpen))}
                    sx={{
                        '& .MuiDrawer-paper': {
                            mt: matchDownMd ? 0 : 11,
                            zIndex: 1099,
                            width: drawerWidth,
                            background: theme.palette.background.default,
                            color: theme.palette.text.primary,
                            borderRight: 'none'
                        }
                    }}
                    ModalProps={{ keepMounted: true }}
                    color="inherit"
                >
                    {matchDownMd && logo}
                    {drawer}
                </Drawer>
            ) : (
                <MiniDrawerStyled variant="permanent" open={drawerOpen}>
                    {logo}
                    {drawer}
                </MiniDrawerStyled>
            )}
            {open && <KeyFormDialog open={open} handleClose={handleClose} />}
            {matchDownMd ? (
                <MobilePromotionDialog open={showPromotion} handleClose={handleClosePromotion} />
            ) : (
                <PromotionDialog open={showPromotion} handleClose={handleClosePromotion} />
            )}
        </Box>
    );
};

export default memo(Sidebar);
